import { queryOptions, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api-client";
import {
  fetchOpportunitySets,
  fetchIndustries,
  fetchProducts,
  fetchTenantProductTypes,
  fetchAdPlatforms,
  fetchCompetitorOptions,
  fetchCompetitorPlatformOptions,
  fetchIndustryProducts,
  createOpportunitySet,
  renameOpportunitySet,
  deleteOpportunitySet,
  retryOpportunitySet,
  type BrandOption,
  type DataScope,
  type IndustryCatalog,
  type OpportunitySetWithCounts,
  type CreateOpportunitySetParams,
} from "@/features/opportunities/api/opportunity-sets.api";
import { dismissTask } from "@/features/task-progress/api/task-progress.api";
import type { TaskRecord } from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { ensureActiveTenant } from "@/features/auth/stores/tenant-switch-guard.store";
import { opportunityKeys } from "./opportunities.queries.js";
import { tenantSettingsKeys } from "./tenant-settings.queries.js";
import { taskProgressKeys } from "./task-progress.queries.js";
import { useTaskSidebarStore } from "@/features/task-progress/store/task-sidebar.store";
import { resolveSourceFilter } from "@nexus/contracts";

/**
 * Surface the freshly-created generation task in the sidebar immediately — the
 * BFF registers the task record before responding, so a refetch picks it up
 * without waiting for the next discovery poll. Also opens the sidebar.
 */
export function revealGenerationTask(queryClient: QueryClient): void {
  const email = useAuth.getState().user?.email;
  if (email) {
    void queryClient.invalidateQueries({ queryKey: taskProgressKeys.active(email) });
  }
  useTaskSidebarStore.getState().expand();
}

/** Poll cadence while any visible set is GENERATING. Kept off hot-path logs. */
export const GENERATION_POLL_MS = 10_000;

// ─── Key Factory ────────────────────────────────────────────────────

export type { BrandOption, DataScope, IndustryCatalog };

export const opportunitySetKeys = {
  all: ["opportunity-sets"] as const,
  /** List is cached per tenant — include `activeTenantId` from auth. */
  list: (tenantId: string) => ["opportunity-sets", "list", tenantId] as const,
  industries: (scope: DataScope, tenantId: string, catalog?: IndustryCatalog) =>
    catalog === undefined
      ? (["opportunity-sets", "industries", scope, tenantId] as const)
      : (["opportunity-sets", "industries", scope, tenantId, catalog] as const),
  productTypes: (scope: DataScope, tenantId: string) =>
    ["opportunity-sets", "product-types", scope, tenantId] as const,
  adPlatforms: (tenantId: string) => ["opportunity-sets", "ad-platforms", tenantId] as const,
  competitorOptions: (scope: DataScope, tenantId: string, industry: string, source = "All") =>
    ["opportunity-sets", "competitor-options", scope, tenantId, industry, source] as const,
  competitorPlatformOptions: (scope: DataScope, tenantId: string, industry: string) =>
    ["opportunity-sets", "competitor-platform-options", scope, tenantId, industry] as const,
  industryProducts: (scope: DataScope, tenantId: string, industry: string) =>
    ["opportunity-sets", "industry-products", scope, tenantId, industry] as const,
} as const;

const ALL_DATA_SCOPES: DataScope[] = [
  "dashboard",
  "overview",
  "drilldown",
  "opportunities",
  "persona-opportunities",
];

/** Invalidate industries cache for a tenant across every page scope (tenant industry mapping changed). */
export function invalidateIndustriesForTenant(queryClient: QueryClient, tenantId: string): void {
  for (const scope of ALL_DATA_SCOPES) {
    void queryClient.invalidateQueries({ queryKey: opportunitySetKeys.industries(scope, tenantId) });
  }
}

/** Invalidate product-type catalog cache after Identity Hub data-sync completes. */
export function invalidateProductsForTenant(queryClient: QueryClient, tenantId: string): void {
  for (const scope of ALL_DATA_SCOPES) {
    void queryClient.invalidateQueries({ queryKey: opportunitySetKeys.productTypes(scope, tenantId) });
  }
}

/** Matches the tenant segment used by `opportunitySetsQueryOptions()` cache keys. */
export function normalizedOpportunitySetTenantId(tenantId: string | null | undefined): string {
  const tid = tenantId ?? "";
  return tid || "__none__";
}

/** Invalidate scripts for a set and the tenant's set-list metadata (tab badge counts). */
export function invalidateScriptAndSetCaches(
  queryClient: QueryClient,
  params: { setId: string; tenantId: string | null | undefined },
): void {
  void queryClient.invalidateQueries({ queryKey: opportunityKeys.scripts(params.setId) });
  void queryClient.invalidateQueries({
    queryKey: opportunitySetKeys.list(normalizedOpportunitySetTenantId(params.tenantId)),
  });
}

// ─── Query Options ──────────────────────────────────────────────────

export function opportunitySetsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: opportunitySetKeys.list(normalizedOpportunitySetTenantId(tid)),
    queryFn: () => fetchOpportunitySets({ tenantId: tid }),
    enabled: !!tid,
    // Polling is a pure function of server state: keep refetching while any set
    // is GENERATING, stop otherwise. No FE-side timers or timeouts.
    // Transient errors keep polling alive so an optimistically-inserted generating
    // card doesn't freeze forever when a single poll fails.
    refetchInterval: (query) => {
      if (query.state.status === "error") return GENERATION_POLL_MS;
      const data = query.state.data as OpportunitySetWithCounts[] | undefined;
      return data?.some((s) => s.status === "GENERATING") ? GENERATION_POLL_MS : false;
    },
  });
}

export function industriesQueryOptions(
  scope: DataScope,
  tenantId: string | null | undefined,
  catalog: IndustryCatalog = "eligible",
) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: opportunitySetKeys.industries(scope, tid || "__none__", catalog),
    queryFn: () => fetchIndustries(scope, catalog),
    enabled: !!tid,
    staleTime: 1000 * 60 * 60, // 1 hour — list is static per tenant until BigQuery wired
  });
}

export function productsQueryOptions(
  scope: DataScope,
  tenantId: string | null | undefined,
) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: opportunitySetKeys.productTypes(scope, tid || "__none__"),
    queryFn: () => fetchTenantProductTypes(scope),
    enabled: !!tid,
    staleTime: 1000 * 60 * 60,
  });
}

export function platformProductsQueryOptions(
  scope: DataScope,
  accountIds: string[],
  platforms: string[],
) {
  const plt = platforms.length === 1 ? platforms[0] : "";
  const sortedIds = [...accountIds].sort().join(",");
  const sortedPlatforms = [...platforms].sort().join(",");
  return queryOptions({
    queryKey: ["opportunity-sets", "products", scope, "accounts", sortedIds, sortedPlatforms || "__all__"] as const,
    queryFn: () => fetchProducts(scope, accountIds, plt),
    enabled: accountIds.length > 0,
    staleTime: 1000 * 60 * 60,
  });
}

export function adPlatformsQueryOptions(tenantId: string | null | undefined) {
  const tid = tenantId ?? "";
  return queryOptions({
    queryKey: opportunitySetKeys.adPlatforms(tid || "__none__"),
    queryFn: fetchAdPlatforms,
    enabled: !!tid,
    staleTime: 1000 * 60 * 60,
  });
}

export function industryProductsQueryOptions(
  scope: DataScope,
  tenantId: string | null | undefined,
  industry: string | null | undefined,
) {
  const tid = tenantId ?? "";
  const ind = industry ?? "";
  return queryOptions({
    queryKey: opportunitySetKeys.industryProducts(scope, tid || "__none__", ind || "__none__"),
    queryFn: ({ signal }) => fetchIndustryProducts(scope, ind, signal),
    enabled: !!tid && !!ind,
    staleTime: 30 * 60 * 1000,
    // The fetcher throws on a FAILED job or an exhausted poll budget; both are settled
    // outcomes, so retrying only re-runs the poll.
    retry: false,
  });
}

export function competitorPlatformOptionsQueryOptions(
  scope: DataScope,
  tenantId: string | null | undefined,
  industry: string | null | undefined,
) {
  const tid = tenantId ?? "";
  const ind = industry ?? "";
  return queryOptions({
    queryKey: opportunitySetKeys.competitorPlatformOptions(scope, tid || "__none__", ind || "__none__"),
    queryFn: ({ signal }) => fetchCompetitorPlatformOptions(scope, ind, signal),
    enabled: !!tid && !!ind,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
}

export function competitorOptionsQueryOptions(
  scope: DataScope,
  tenantId: string | null | undefined,
  industry: string | null | undefined,
  platforms?: string[],
) {
  const tid = tenantId ?? "";
  const ind = industry ?? "";
  const source = resolveSourceFilter(platforms);
  return queryOptions({
    queryKey: opportunitySetKeys.competitorOptions(scope, tid || "__none__", ind || "__none__", source),
    queryFn: ({ signal }) => fetchCompetitorOptions(scope, ind, signal, platforms),
    enabled: !!tid && !!ind,
    staleTime: 30 * 60 * 1000, // 30 min — BQ results change infrequently
    retry: false,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────

export function useCreateOpportunitySet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: CreateOpportunitySetParams) => createOpportunitySet(params),
    onSuccess: () => {
      revealGenerationTask(queryClient);
      const activeTenantId = useAuth.getState().activeTenantId;
      if (activeTenantId) {
        void queryClient.invalidateQueries({
          queryKey: opportunitySetKeys.list(activeTenantId),
        });
        void queryClient.invalidateQueries({
          queryKey: opportunitySetKeys.productTypes("opportunities", activeTenantId),
        });
        void queryClient.invalidateQueries({ queryKey: tenantSettingsKeys.settings(activeTenantId) });
        void queryClient.invalidateQueries({ queryKey: tenantSettingsKeys.competitors(activeTenantId) });
      }
    },
  });
}

export function useRenameOpportunitySet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      renameOpportunitySet(id, name),
    onSuccess: () => {
      const activeTenantId = useAuth.getState().activeTenantId;
      if (activeTenantId) {
        void queryClient.invalidateQueries({
          queryKey: opportunitySetKeys.list(activeTenantId),
        });
      }
    },
  });
}

export function useDeleteOpportunitySet() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteOpportunitySet(id),
    onSuccess: () => {
      const activeTenantId = useAuth.getState().activeTenantId;
      if (activeTenantId) {
        void queryClient.invalidateQueries({
          queryKey: opportunitySetKeys.list(activeTenantId),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: ["opportunities", "list"],
      });
      void queryClient.invalidateQueries({
        queryKey: opportunityKeys.all,
      });
    },
  });
}

/**
 * Retry a failed opportunity-set generation from the task-progress sidebar.
 * Dismisses the old failed task card and triggers a new generation, then opens
 * the sidebar to the new card (via revealGenerationTask).
 */
export function useRetryOpportunitySetTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (task: TaskRecord) => {
      const setId = task.navigation?.params?.setId;
      if (!setId) throw new Error("Task has no associated set ID");
      // authFetch reads activeTenantId from the Zustand store synchronously.
      // Switch to the task's tenant now so the X-Tenant-Id header is correct
      // even if the user is currently viewing a different tenant's page.
      if (task.tenantId) await ensureActiveTenant(task.tenantId);
      return retryOpportunitySet(setId);
    },
    onSuccess: (_updated, task) => {
      // Dismiss the old failed task card before revealing the new one.
      void dismissTask(task.correlationId).catch(() => {});
      const email = useAuth.getState().user?.email;
      if (email) {
        queryClient.setQueryData<TaskRecord[]>(
          taskProgressKeys.active(email),
          (prev) => prev?.filter((t) => t.correlationId !== task.correlationId),
        );
      }
      revealGenerationTask(queryClient);
      const activeTenantId = useAuth.getState().activeTenantId;
      if (activeTenantId) {
        void queryClient.invalidateQueries({ queryKey: opportunitySetKeys.list(activeTenantId) });
      }
    },
    onError: (error, task) => {
      // The task card's setId points to a set that was already soft-deleted (stale
      // card — e.g. a previous retry replaced the set and dismissTask failed silently).
      // Silently dismiss the stale card rather than surfacing a confusing 404.
      const isNotFound = error instanceof ApiError && error.status === 404;
      if (isNotFound) {
        void dismissTask(task.correlationId).catch(() => {});
        const email = useAuth.getState().user?.email;
        if (email) {
          queryClient.setQueryData<TaskRecord[]>(
            taskProgressKeys.active(email),
            (prev) => prev?.filter((t) => t.correlationId !== task.correlationId),
          );
        }
      }
    },
  });
}
